import { buildThumbnailNode } from '../thumbnail/core';
import { wireLocationDescriptionActions } from './location-description-actions';
import { editLocationUrl, listText, locationPictureUrl, locationUrl, type LocationListResponse, type LocationListRow } from './location-list-data';
import type { CatalogListConfig } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { appendIconAction, appendIconLink, appendTableCell as td, catalogIcon as icon } from './dom-helpers';

/** Copy is a state change: a POST form carrying the location id + the session CSRF token, never a GET link. */
function appendCopyForm(parent: HTMLElement, loc: LocationListRow, csrfToken: string): void {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = resolveUrl('options/tools/locations/copy_location_inc.php');
    form.className = 'd-inline m-0';
    const fields: ReadonlyArray<readonly [string, string]> = [['location_id', loc.location_id], ['csrf_token', csrfToken]];
    for (const [name, value] of fields) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }
    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'btn btn-sm btn-secondary border border-dark m-1';
    button.setAttribute('aria-label', 'Copy location');
    button.innerHTML = icon('copy', 20, 20, 'Copy location');
    form.appendChild(button);
    parent.appendChild(form);
}

function settingsCell(cell: HTMLElement, loc: LocationListRow, csrfToken: string): void {
    const row1 = document.createElement('div');
    row1.className = 'd-flex align-items-center justify-content-center col-12';
    cell.appendChild(row1);
    appendIconLink(row1, editLocationUrl(loc.location_id), 'btn btn-sm btn-info border border-dark m-1', 'edit', 'Edit location');
    appendCopyForm(row1, loc, csrfToken);

    const row2 = document.createElement('div');
    row2.className = 'd-flex align-items-center justify-content-center col-12';
    cell.appendChild(row2);

    const publicBtn = appendIconAction(row2, 'btn btn-primary btn-sm m-1', 'public', 'Make private', async () => {
        const result = await queryData(resolveUrl('options/db_queries/update/update_privacy_inc.php'), 'div_sidelog_warning', { item_type: 'location', location_id: loc.location_id, location_private: '1', csrf_token: csrfToken });
        if (result === null) return;
        publicBtn.style.display = 'none';
        privateBtn.style.display = '';
    });
    const privateBtn = appendIconAction(row2, 'btn btn-danger btn-sm m-1', 'private', 'Make public', async () => {
        const result = await queryData(resolveUrl('options/db_queries/update/update_privacy_inc.php'), 'div_sidelog_warning', { item_type: 'location', location_id: loc.location_id, location_private: '0', csrf_token: csrfToken });
        if (result === null) return;
        privateBtn.style.display = 'none';
        publicBtn.style.display = '';
    });
    publicBtn.style.display = Number(loc.location_private) === 1 ? 'none' : '';
    privateBtn.style.display = Number(loc.location_private) === 1 ? '' : 'none';

    const del = appendIconAction(row2, 'btn btn-warning btn-sm m-1', 'delete', 'Delete location', () => {
        del.style.display = 'none';
        confirm.style.display = '';
    });
    const confirm = appendIconAction(row2, 'btn btn-danger btn-sm m-1', 'confirm', 'Confirm delete location', async () => {
        if (typeof openSideLog === 'function') openSideLog();
        await queryData(resolveUrl('options/db_queries/delete/soft_delete_inc.php'), 'div_sidelog_warning', { item_type: 'location', location_id: loc.location_id, display_success_sidelog: '1', csrf_token: csrfToken });
        document.getElementById(`tr_${loc.location_id}`)?.remove();
    });
    confirm.style.display = 'none';
}

function checkboxCell(row: HTMLTableRowElement, itemId: string): void {
    const check = td(row, 'position-relative p-0');
    const input = document.createElement('input');
    input.id = itemId;
    input.className = 'position-absolute top-50 start-50 z-3';
    input.type = 'checkbox';
    input.name = itemId;
    input.value = '1';
    input.style.transform = 'translate(-50%, -50%) scale(1.8)';
    check.appendChild(input);
    const label = document.createElement('label');
    label.htmlFor = itemId;
    label.className = 'position-absolute top-0 start-0 w-100 h-100';
    label.style.cursor = 'pointer';
    label.style.zIndex = '2';
    check.appendChild(label);
}

function appendPicture(cell: HTMLElement, loc: LocationListRow, map: boolean): void {
    const url = locationPictureUrl(loc, map);
    if (!url) return;
    const picture = buildThumbnailNode({ directUrl: url, item_id: loc.location_id, name: listText(loc.location_name), w: 90, h: 90 });
    if (map) {
        const link = document.createElement('a');
        link.href = resolveUrl(`tools/locations/view_map.php?location_id=${encodeURIComponent(loc.location_id)}`);
        link.appendChild(picture);
        cell.appendChild(link);
    } else cell.appendChild(picture);
}

function renderRow(loc: LocationListRow, data: LocationListResponse, bulk: boolean, readable: Set<string>): HTMLTableRowElement {
    const template = data.mode === 'template';
    const guest = data.mode === 'guest-sample';
    const manage = data.mode === 'my';
    const row = document.createElement('tr');
    row.id = `tr_${loc.location_id}`;
    row.dataset.ftLocationId = loc.location_id;
    const nameText = listText(loc.location_name);
    const parentText = readable.has(String(loc.location_parent_id).toUpperCase()) ? listText(loc.location_parent_name) : '';
    const zone = loc.location_zone_prefix === '0' ? '' : listText(loc.location_zone_prefix);
    row.dataset.ftLocationText = `${nameText} ${parentText} ${zone}`.trim().toLowerCase();

    if (template || (manage && bulk)) checkboxCell(row, loc.location_id);
    if (manage) settingsCell(td(row, 'text-center'), loc, data.csrf_token || '');
    if (!guest) {
        appendPicture(td(row, 'text-center'), loc, true);
        appendPicture(td(row, 'text-center'), loc, false);
    }

    const nameCell = td(row, 'text-start');
    nameCell.dataset.ftLocationCell = 'name';
    const name = document.createElement('a');
    name.href = locationUrl(loc.location_id);
    name.className = template ? 'link-primary h5' : 'link-primary';
    name.textContent = nameText;
    nameCell.appendChild(name);
    if (template && loc.location_description && loc.location_description !== '0') {
        const hr = document.createElement('hr');
        hr.className = 'border';
        nameCell.appendChild(hr);
        // Feed authored rich text to the existing parser, exactly as the old PHP mount did.
        const desc = document.createElement('span');
        desc.className = 'ft-pt-mount';
        const blob = document.createElement('script');
        blob.type = 'application/json';
        blob.className = 'ft-pt-blob';
        blob.textContent = JSON.stringify({ text: loc.location_description });
        desc.appendChild(blob);
        const description = document.createElement('span');
        description.dataset.ftLocationDescription = '1';
        description.appendChild(desc);
        nameCell.appendChild(description);
    }

    const type = td(row, guest ? '' : 'text-center');
    type.dataset.ftLocationCell = 'type';
    const typeLabel = listText(loc.location_type_name);
    type.dataset.sort = typeLabel || loc.location_type || '';
    if (guest) {
        const glyph = document.createElement('span');
        glyph.innerHTML = icon(loc.location_type || '', 18, 18, false);
        type.append(glyph, document.createTextNode(` ${typeLabel}`));
    } else if (loc.location_type) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn btn-sm p-0 border-0 bg-transparent';
        b.setAttribute('aria-label', typeLabel || 'Location type');
        b.innerHTML = icon(loc.location_type, 25, 25, typeLabel || false);
        b.addEventListener('click', () => { if (typeof openItemInfo === 'function') openItemInfo(loc.location_type!); });
        type.appendChild(b);
    }
    if (!template && !guest) {
        const parent = td(row);
        parent.dataset.ftLocationCell = 'parent';
        if (loc.location_parent_id) {
            const a = document.createElement('a');
            a.href = locationUrl(loc.location_parent_id);
            a.className = 'link-primary';
            a.textContent = parentText;
            parent.appendChild(a);
        }
    }
    if (!guest) {
        const cell = td(row, 'text-end');
        cell.dataset.ftLocationCell = 'zone';
        cell.textContent = zone;
    }
    return row;
}

function build(container: HTMLElement, data: LocationListResponse, searchInput: HTMLInputElement | null, emptyText: string, bulk: boolean): void {
    if (data.schema !== 'location-list-v1' || !Array.isArray(data.locations)) throw new Error('location-list-schema');
    container.replaceChildren();
    const template = data.mode === 'template';
    const manage = data.mode === 'my';
    const guest = data.mode === 'guest-sample';
    const browse = !template && !manage;
    const wrap = document.createElement('div');
    wrap.className = guest ? 'table-responsive' : 'table-responsive-xxl';
    const table = document.createElement('table');
    table.className = 'ft-sortable';
    if (browse && searchInput) table.dataset.ftSortableSearch = searchInput.id;
    const thead = document.createElement('thead');
    const header = document.createElement('tr');
    const th = (key: string, label: string, unsorted = false, unfiltered = false, width = ''): void => {
        const cell = document.createElement('th');
        cell.className = `${guest ? '' : 'text-center'}${unsorted ? ' ft-no-sort' : ''}${unfiltered || (browse && searchInput) ? ' ft-no-filter' : ''}`;
        cell.setAttribute('aria-label', label);
        if (width && !guest) cell.style.width = width;
        cell.innerHTML = guest ? '' : icon(key);
        if (guest) cell.textContent = label;
        header.appendChild(cell);
    };
    if (template || (manage && bulk)) th('checkbox', 'Select', true, true, '75px');
    if (manage) th('settings', 'Settings', true, true, '100px');
    if (!guest) {
        th('map', 'Map', true, true, '100px');
        th('picture', 'Picture', true, true, template ? '100px' : '');
    }
    th('location', 'Location');
    th('location_type', guest ? 'Type' : 'Location Type', false, !guest, '1%');
    if (!template && !guest) th('location_parent', 'Parent Location');
    if (!guest) th('location_zone_prefix', 'Zone Prefix', false, false, template ? '50px' : '');
    thead.appendChild(header);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    const readable = new Set((data.readable_parent_ids || []).map(id => id.toUpperCase()));
    const rows = data.locations.map(loc => renderRow(loc, data, bulk, readable));
    rows.forEach(row => tbody.appendChild(row));
    table.appendChild(tbody);
    wrap.appendChild(table);
    container.appendChild(wrap);
    const empty = document.createElement('div');
    empty.className = 'text-center text-muted py-5';
    empty.textContent = emptyText;
    container.appendChild(empty);
    if (template) wireLocationDescriptionActions(container);
    const apply = (): void => {
        const q = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        rows.forEach(row => {
            const show = !q || (row.dataset.ftLocationText || '').includes(q);
            row.style.display = show ? '' : 'none';
            if (show) visible++;
        });
        wrap.style.display = browse || visible ? '' : 'none';
        empty.style.display = browse || visible ? 'none' : '';
    };
    // Browse tables retain ft-sortable's combined column/global filters. Management tables keep their existing search.
    if (!browse) searchInput?.addEventListener('input', apply);
    initTooltips(container);
    const mounts = window as Window & { ftMountParsedText?: (root: Element) => void };
    mounts.ftMountParsedText?.(container);
    if (typeof window.ftSortableScan === 'function') window.ftSortableScan();
    if (!browse) apply();
    else empty.style.display = 'none';
}

export function mountCatalogMyLocations(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) return;
    const searchInput = config.searchInputId ? (document.getElementById(config.searchInputId) as HTMLInputElement | null) : null;
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No locations found.';
    const bulk = root.getAttribute('data-ft-bulk') === '1';
    void (async () => {
        const timer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json() as LocationListResponse;
            clearTimeout(timer);
            hideLoader(root);
            setStatus(root, '');
            build(container, data, searchInput, emptyText, bulk);
        } catch (e) {
            clearTimeout(timer);
            hideLoader(root);
            console.error('[my-locations] fetch/render failed', e);
            setStatus(root, 'Could not load locations. Please try again.');
        }
    })();
}
